import { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import HomeIcon from '../assets/h-icon.svg';
import CartIcon from '../assets/c-icon.svg';
import ProfileIcon from '../assets/p-icon.svg';
import OrderIcon from '../assets/o-icon.svg';
import { useTranslation } from 'react-i18next';
import { useUser } from '../contexts/user';
import { useCart } from '../contexts/cart';
import LoginModal from './LoginModal';
import LoginModalHome from './LoginModalHome';
import SignupModal from './SignupModal';
import api from '../api/api';
import log from '../utils/logger';

const MobileNavigation = () => {
  const location = useLocation();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user, idToken } = useUser();
  const { uniqueItems } = useCart();
  const [isLoginModalOpen, setIsLoginModalOpen] = useState(false);
  const [isLoginHomeModalOpen, setIsLoginHomeModalOpen] = useState(false);
  const [isSignupModalOpen, setIsSignupModalOpen] = useState(false);
  const [userEmail, setUserEmail] = useState('');

  const handleEmailSubmit = async (email) => {
    try {
      const response = await api.post('/user/check-email', { email });
      const { exists } = response.data.response.data;

      if (exists) {
        setIsLoginHomeModalOpen(true);
        setIsSignupModalOpen(false);
      } else {
        setIsLoginHomeModalOpen(false);
        setIsSignupModalOpen(true);
      }
    } catch (error) {
      log.error('Email check failed:', error);
    }
  };

  const navItems = [
    { to: '/home', label: t('nav.home'), icon: HomeIcon },
    { to: '/profile/orders', label: t('nav.reorder'), icon: OrderIcon },
    { to: '/checkout', label: t('nav.cart'), icon: CartIcon },
    { to: '/profile', label: t('nav.profile'), icon: ProfileIcon },
  ];

  const getLinkClasses = (path) => {
    return location.pathname === path ? 'text-black opacity-100' : 'text-[#919191] opacity-50';
  };

  return (
    <>
      <nav className="bg-white h-[64px] sm:h-[70px] fixed bottom-0 w-full lg:hidden z-40 grid grid-cols-4 items-center justify-items-center border-t border-gray-200" style={{ paddingBottom: 'max(0px, env(safe-area-inset-bottom))' }}>
        {navItems.map(({ to, label, icon }) => {
          const requiresLogin = label === t('nav.profile') || label === t('nav.reorder');
          const isCart = label === t('nav.cart');
          const isReorder = label === t('nav.reorder');

          // Reorder uses a slightly larger asset to compensate for whitespace inside the SVG.
          const iconSize = isReorder
            ? 'h-7 w-7 sm:h-8 sm:w-8'
            : 'h-6 w-6 sm:h-[26px] sm:w-[26px]';

          if (requiresLogin) {
            return (
              <button
                key={to}
                aria-label={label}
                onClick={() => {
                  if (user && idToken) {
                    navigate(to);
                  } else {
                    setIsLoginModalOpen(true);
                  }
                }}
                className="flex flex-col items-center justify-center gap-1 w-full h-full min-h-[44px] min-w-[44px] px-1"
              >
                <img
                  src={icon}
                  alt=""
                  aria-hidden="true"
                  className={`transition-opacity duration-200 ${iconSize} ${
                    isReorder ? 'opacity-60' : ''
                  } ${getLinkClasses(to)}`}
                />
                <p className={`text-[10px] sm:text-[11px] uppercase font-medium leading-tight text-center truncate max-w-full ${getLinkClasses(to)}`}>
                  {label}
                </p>
              </button>
            );
          }

          return (
            <Link
              key={to}
              to={to}
              aria-label={label}
              className="flex flex-col items-center justify-center gap-1 relative w-full h-full min-h-[44px] min-w-[44px] px-1"
            >
              <img
                src={icon}
                alt=""
                aria-hidden="true"
                className={`transition-opacity duration-200 ${iconSize} ${getLinkClasses(to)}`}
              />

              {isCart && uniqueItems > 0 && (
                <span className="absolute top-1 right-[22%] h-2 w-2 bg-red-500 rounded-full"></span>
              )}

              <p className={`text-[10px] sm:text-[11px] uppercase font-medium leading-tight text-center truncate max-w-full ${getLinkClasses(to)}`}>
                {label}
              </p>
            </Link>
          );
        })}
      </nav>

      <LoginModal
        isOpen={isLoginModalOpen}
        onClose={() => setIsLoginModalOpen(false)}
        onSubmit={(email) => {
          setUserEmail(email);
          setIsLoginModalOpen(false);
          handleEmailSubmit(email);
        }}
      />

      <LoginModalHome
        isOpen={isLoginHomeModalOpen}
        onClose={() => setIsLoginHomeModalOpen(false)}
        email={userEmail}
      />

      {isSignupModalOpen && (
        <SignupModal
          onClose={() => setIsSignupModalOpen(false)}
          email={userEmail}
          onAlreadyAccount={() => {
            setIsSignupModalOpen(false);
            setIsLoginModalOpen(true);
          }}
        />
      )}
    </>
  );
};

export default MobileNavigation;
